<script setup>
import { reactive, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import camelcaseKeys from 'camelcase-keys';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  processContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';
import AttendanceWarningDialog from 'dashboard/components-next/NewConversation/components/AttendanceWarningDialog.vue';
import {
  useAttendanceGate,
  ATTENDANCE_VARIANT,
} from 'dashboard/composables/useAttendanceGate';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  alignPosition: {
    type: String,
    default: 'left',
  },
  contactId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close']);

const searchContacts = createContactSearcher();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const isWhatsAppValidationMessage = message =>
  typeof message === 'string' && message.toLowerCase().includes('whatsapp');

const whatsappAlertAction = message =>
  isWhatsAppValidationMessage(message)
    ? {
        variant: 'error',
        icon: 'i-lucide-message-circle-x',
        duration: 5000,
      }
    : null;

// Traduz mensagens de validação do ActiveRecord (Rails) que chegam em inglês
// para as chaves i18n pt-BR equivalentes. Fallback: mensagem genérica.
const resolveContactCreateErrorMessage = error => {
  const rawMessage = error?.response?.data?.message || '';
  const lower = rawMessage.toLowerCase();

  if (/phone[_ ]?number.*(already|taken)/.test(lower)) {
    return t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE');
  }
  if (/email.*(already|taken)/.test(lower)) {
    return t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE');
  }
  if (/identifier.*(already|taken)/.test(lower)) {
    return t('CONTACT_FORM.FORM.CONTACT_IDENTIFIER.DUPLICATE');
  }

  return t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE');
};

// Always render as centered modal overlay (desktop + mobile)
// Legacy popover behavior removed for consistent UX.
const viewInModal = computed(() => true);

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);

const formState = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const clearFormState = () => {
  Object.assign(formState, {
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
};

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const { resolveGate } = useAttendanceGate();
// Modo de pedido ligado? getCurrentAccount (auth store) vem SEM settings — a
// fonte com settings é useAccount().currentAccount (mesma do AssignToReplyDialog
// e do useContactAttendance).
const { currentAccount } = useAccount();
const takeoverGateEnabled = computed(() =>
  Boolean(currentAccount.value?.settings?.takeover_request_gate)
);
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const composePopoverClass = computed(() => {
  if (viewInModal.value) return '';

  return props.alignPosition === 'right'
    ? 'absolute ltr:left-0 ltr:right-[unset] rtl:right-0 rtl:left-[unset]'
    : 'absolute rtl:left-0 rtl:right-[unset] ltr:right-0 ltr:left-[unset]';
});

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      const results = await searchContacts(query);
      // null means the request was aborted (a newer search is in-flight),
      if (results === null) return;
      contacts.value = results;
      isSearching.value = false;
    } catch (error) {
      isSearching.value = false;
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    }
  },
  400,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      const rawMessage = error?.response?.data?.message || '';
      const message = resolveContactCreateErrorMessage(error);
      // Passa rawMessage para whatsappAlertAction: a heurística busca "whatsapp"
      // no texto e a tradução pt-BR pode não conter esse token.
      useAlert(message, whatsappAlertAction(rawMessage));
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  contacts.value = [];
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      // Merge the processed contactableInboxes with the inboxesList
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );

      isFetchingInboxes.value = false;
    } catch (error) {
      isFetchingInboxes.value = false;
    }
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  if (!inbox) clearFormState();
  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
  clearFormState();
};

const closeCompose = () => {
  showComposeNewConversation.value = false;
  if (!props.contactId) {
    // If contactId is passed as prop
    // Then don't allow to remove the selected contact
    selectedContact.value = null;
  }
  targetInbox.value = null;
  resetContacts();
  emit('close');
};

const discardCompose = () => {
  clearFormState();
  formState.message = '';
  closeCompose();
};

const performCreateConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    let data;
    const contactId = payload.contactId;
    const inboxId = payload.inboxId;

    if (contactId && inboxId) {
      await store.dispatch('contactConversations/get', contactId);
      const existingConversation = store.getters[
        'contactConversations/getActiveConversationByInbox'
      ](contactId, inboxId);

      if (existingConversation) {
        data = await store.dispatch(
          'contactConversations/sendToExistingConversation',
          {
            conversation: existingConversation,
            params: payload,
          }
        );
      }
    }

    if (!data) {
      data = await store.dispatch('contactConversations/create', {
        params: payload,
        isFromWhatsApp,
      });
    }

    const accountId = data.account_id ?? data.accountId;
    const conversationId = data.id;
    discardCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'));
    if (accountId && conversationId) {
      router.push(`/app/accounts/${accountId}/conversations/${conversationId}`);
    }
    return true; // Return success
  } catch (error) {
    const message =
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE');
    useAlert(message, whatsappAlertAction(message));
    return false; // Return failure
  }
};

// GATE DA FASE 2 — o ponto em que o sistema para de mandar mensagem calado.
// Até aqui, quando o contato já tinha conversa ativa, o fluxo acima reusava a
// conversa em silêncio (sendToExistingConversation): o agente achava que estava
// abrindo um atendimento novo e na verdade escrevia no meio do atendimento de um
// colega — sem nunca ver o nome dele.
const attendanceDialogRef = ref(null);
const attendanceGate = ref({
  variant: null,
  attendance: null,
  sameInbox: false,
});
const pendingConversation = ref(null);

const createConversation = async ({ payload, isFromWhatsApp }) => {
  const gate = await resolveGate({
    contactId: payload.contactId,
    inboxId: payload.inboxId,
  });

  if (gate.blocked) {
    attendanceGate.value = gate;
    pendingConversation.value = { payload, isFromWhatsApp };
    attendanceDialogRef.value?.open();
    // false = o form NÃO limpa a mensagem — ela precisa sobreviver p/ o
    // "Enviar mesmo assim".
    return false;
  }

  return performCreateConversation({ payload, isFromWhatsApp });
};

// Com o modo ligado + dono HUMANO, o aviso vira validação: sem "enviar mesmo
// assim"; a saída é Solicitar (se eu alcanço) ou nada (outro time).
const isHumanTakeover = computed(
  () => attendanceGate.value.variant === ATTENDANCE_VARIANT.HUMAN
);
const attendanceCanRequest = computed(
  () =>
    takeoverGateEnabled.value &&
    isHumanTakeover.value &&
    Boolean(attendanceGate.value.attendance?.canRequest) &&
    !attendanceGate.value.attendance?.myPendingRequestId
);
const attendanceHasPendingRequest = computed(
  () =>
    takeoverGateEnabled.value &&
    isHumanTakeover.value &&
    Boolean(attendanceGate.value.attendance?.myPendingRequestId)
);
const attendanceAllowSendAnyway = computed(
  () => !(takeoverGateEnabled.value && isHumanTakeover.value)
);
const isRequestingAttendance = ref(false);

const onAttendanceCancel = () => {
  attendanceDialogRef.value?.close();
  pendingConversation.value = null;
};

const onAttendanceRequest = async reason => {
  const conversationId = attendanceGate.value?.attendance?.id;
  if (!conversationId || isRequestingAttendance.value) return;
  isRequestingAttendance.value = true;
  try {
    await store.dispatch('takeoverRequests/create', { conversationId, reason });
    useAlert(t('CONTACTS_LAYOUT.IN_ATTENDANCE.REQUEST.SENT'));
    attendanceDialogRef.value?.close();
    pendingConversation.value = null;
    discardCompose();
  } catch (error) {
    const code = error?.response?.data?.error_code;
    useAlert(
      code === 'takeover_request_exists'
        ? t('CONTACTS_LAYOUT.IN_ATTENDANCE.REQUEST.ERRORS.EXISTS')
        : t('CONTACTS_LAYOUT.IN_ATTENDANCE.REQUEST.ERRORS.GENERIC')
    );
  } finally {
    isRequestingAttendance.value = false;
  }
};

const onAttendanceSendAnyway = async () => {
  const pending = pendingConversation.value;
  attendanceDialogRef.value?.close();
  pendingConversation.value = null;
  if (pending) await performCreateConversation(pending);
};

const onAttendanceGoToConversation = () => {
  const attendance = attendanceGate.value?.attendance;
  attendanceDialogRef.value?.close();
  pendingConversation.value = null;
  if (!attendance?.id) return;
  // attendance.id é o display_id — é o que a rota de conversa espera.
  discardCompose();
  router.push(
    `/app/accounts/${currentAccountId.value}/conversations/${attendance.id}`
  );
};

const toggle = () => {
  showComposeNewConversation.value = !showComposeNewConversation.value;
};

watch(
  activeContact,
  (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      // Reset on contact change
      if (currentContact?.id !== previousContact?.id) {
        clearSelectedContact();
        clearFormState();
        formState.message = '';
      }

      // First process the contactable inboxes to get the right structure
      // Store data may use snake_case keys (contact_inboxes) after
      // removing camelcaseKeys normalization from the contacts store.
      const rawInboxes =
        currentContact.contactInboxes || currentContact.contact_inboxes || [];
      const processedInboxes = processContactableInboxes(
        camelcaseKeys(rawInboxes, { deep: true })
      );
      // Then Merge processedInboxes with the inboxes list
      selectedContact.value = {
        ...currentContact,
        contactInboxes: mergeInboxDetails(processedInboxes, inboxesList.value),
      };
    }
  },
  { immediate: true, deep: true }
);

const handleClickOutside = () => {
  if (!showComposeNewConversation.value) return;

  showComposeNewConversation.value = false;
  emit('close');
};

const onModalBackdropClick = () => {
  if (!viewInModal.value) return;
  handleClickOutside();
};

const onNewConversationBusEvent = isActive => {
  if (isActive) {
    showComposeNewConversation.value = true;
  }
};

onMounted(() => {
  resetContacts();
  emitter.on(BUS_EVENTS.NEW_CONVERSATION_MODAL, onNewConversationBusEvent);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.NEW_CONVERSATION_MODAL, onNewConversationBusEvent);
});

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
        emit('close');
        emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    v-on-click-outside="[
      handleClickOutside,
      // Fixed and edge case https://github.com/chatwoot/chatwoot/issues/10785
      // This will prevent closing the compose conversation modal when the editor Create link popup is open
      { ignore: ['dialog.ProseMirror-prompt-backdrop'] },
    ]"
    class="relative"
    :class="{
      'z-50': showComposeNewConversation && !viewInModal,
    }"
  >
    <slot
      name="trigger"
      :is-open="showComposeNewConversation"
      :toggle="toggle"
    />
    <div
      v-if="showComposeNewConversation"
      :class="{
        'fixed z-50 bg-n-alpha-black1 backdrop-blur-[4px] flex items-start pt-[clamp(3rem,15vh,12rem)] justify-center inset-0':
          viewInModal,
      }"
      @click.self="onModalBackdropClick"
    >
      <ComposeNewConversationForm
        :form-state="formState"
        :class="[{ 'mt-2': !viewInModal }, composePopoverClass]"
        :contacts="contacts"
        :contact-id="contactId"
        :is-loading="isSearching"
        :current-user="currentUser"
        :selected-contact="selectedContact"
        :target-inbox="targetInbox"
        :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes"
        :is-direct-uploads-enabled="directUploadsEnabled"
        :contact-conversations-ui-flags="uiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :send-with-signature="sendWithSignature"
        @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts"
        @update-selected-contact="handleSelectedContact"
        @update-target-inbox="handleTargetInbox"
        @clear-selected-contact="clearSelectedContact"
        @create-conversation="createConversation"
        @discard="discardCompose"
      />
    </div>
    <AttendanceWarningDialog
      ref="attendanceDialogRef"
      :variant="attendanceGate.variant"
      :attendance="attendanceGate.attendance"
      :same-inbox="attendanceGate.sameInbox"
      :can-request="attendanceCanRequest"
      :has-pending-request="attendanceHasPendingRequest"
      :allow-send-anyway="attendanceAllowSendAnyway"
      :is-requesting="isRequestingAttendance"
      @go-to-conversation="onAttendanceGoToConversation"
      @send-anyway="onAttendanceSendAnyway"
      @request-attendance="onAttendanceRequest"
      @cancel="onAttendanceCancel"
    />
  </div>
</template>
